import React, { useEffect, useState, useCallback, useRef } from "react";
import { AnimatePresence, motion } from "framer-motion";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import CircularProgress from "@mui/material/CircularProgress";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import CenterFocusStrongIcon from "@mui/icons-material/CenterFocusStrong";

const MIN_ZOOM = 1;
const MAX_ZOOM = 4;
const ZOOM_STEP = 0.2;
const MAX_VIEWPORT_HEIGHT = "95vh";
const MAX_VIEWPORT_WIDTH = "90vw";
const SWIPE_THRESHOLD = 50;
const SWIPE_VERTICAL_LIMIT = 80;

const SetupImageLightbox = ({ open, images = [], startIndex = 0, onClose }) => {
  const [currentIndex, setCurrentIndex] = useState(startIndex);
  const [zoom, setZoom] = useState(MIN_ZOOM);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const panStartRef = useRef({ x: 0, y: 0 });
  const [imageLoaded, setImageLoaded] = useState(false);
  const touchStartRef = useRef(null);
  const touchCurrentRef = useRef(null);

  const currentImage = images[currentIndex] || null;
  const safeIndex = Math.min(Math.max(startIndex, 0), Math.max(images.length - 1, 0));

  useEffect(() => {
    if (open) {
      setCurrentIndex(safeIndex);
      setZoom(MIN_ZOOM);
      setPan({ x: 0, y: 0 });
    }
  }, [open, safeIndex]);

  useEffect(() => {
    if (open) {
      setZoom(MIN_ZOOM);
      setPan({ x: 0, y: 0 });
      setImageLoaded(false);
    }
  }, [currentIndex, open]);

  const goToPrevious = useCallback(() => {
    if (!images.length) return;
    setCurrentIndex((prev) => {
      if (prev <= 0) {
        return 0;
      }
      return prev - 1;
    });
  }, [images.length]);

  const goToNext = useCallback(() => {
    if (!images.length) return;
    setCurrentIndex((prev) => {
      if (prev >= images.length - 1) {
        return images.length - 1;
      }
      return prev + 1;
    });
  }, [images.length]);

  const handleKeyDown = useCallback(
    (event) => {
      if (!open) return;

      switch (event.key) {
        case "Escape":
          onClose?.();
          break;
        case "ArrowLeft":
          goToPrevious();
          break;
        case "ArrowRight":
          goToNext();
          break;
        case "+":
        case "=":
          event.preventDefault();
          setZoom((prev) => Math.min(prev + ZOOM_STEP, MAX_ZOOM));
          break;
        case "-":
        case "_":
          event.preventDefault();
          setZoom((prev) => Math.max(prev - ZOOM_STEP, MIN_ZOOM));
          break;
        default:
          break;
      }
    },
    [goToNext, goToPrevious, onClose, open]
  );

  useEffect(() => {
    if (!open) return;
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [handleKeyDown, open]);

  const zoomIn = useCallback(() => {
    setZoom((prev) => Math.min(prev + ZOOM_STEP, MAX_ZOOM));
  }, []);

  const zoomOut = useCallback(() => {
    setZoom((prev) => Math.max(prev - ZOOM_STEP, MIN_ZOOM));
  }, []);

  const resetZoom = useCallback(() => {
    setZoom(MIN_ZOOM);
    setPan({ x: 0, y: 0 });
  }, []);

  const handleMouseDown = (event) => {
    if (zoom <= 1) return;
    setIsPanning(true);
    panStartRef.current = {
      x: event.clientX - pan.x,
      y: event.clientY - pan.y,
    };
  };

  const handleMouseMove = (event) => {
    if (!isPanning || zoom <= 1) return;
    setPan({
      x: event.clientX - panStartRef.current.x,
      y: event.clientY - panStartRef.current.y,
    });
  };

  const stopPanning = () => setIsPanning(false);

  const handleWheel = (event) => {
    if (!open) return;
    event.preventDefault();
    if (event.deltaY < 0) {
      zoomIn();
    } else {
      zoomOut();
    }
  };

  const handleTouchStart = (event) => {
    if (!event.touches || !event.touches.length) return;
    const touch = event.touches[0];
    touchStartRef.current = { x: touch.clientX, y: touch.clientY };
    touchCurrentRef.current = { x: touch.clientX, y: touch.clientY };

    if (zoom > 1) {
      setIsPanning(true);
      panStartRef.current = {
        x: touch.clientX - pan.x,
        y: touch.clientY - pan.y,
      };
    }
  };

  const handleTouchMove = (event) => {
    if (!event.touches || !event.touches.length) return;
    const touch = event.touches[0];
    touchCurrentRef.current = { x: touch.clientX, y: touch.clientY };

    if (zoom > 1 && isPanning) {
      event.preventDefault();
      setPan({
        x: touch.clientX - panStartRef.current.x,
        y: touch.clientY - panStartRef.current.y,
      });
    }
  };

  const handleTouchEnd = () => {
    if (zoom <= 1 && touchStartRef.current && touchCurrentRef.current) {
      const deltaX = touchCurrentRef.current.x - touchStartRef.current.x;
      const deltaY = touchCurrentRef.current.y - touchStartRef.current.y;
      if (
        Math.abs(deltaX) > SWIPE_THRESHOLD &&
        Math.abs(deltaY) < SWIPE_VERTICAL_LIMIT
      ) {
        if (deltaX < 0) {
          goToNext();
        } else {
          goToPrevious();
        }
      }
    }
    setIsPanning(false);
    touchStartRef.current = null;
    touchCurrentRef.current = null;
  };

  const handleOverlayClick = (event) => {
    if (event.target === event.currentTarget) {
      onClose?.();
    }
  };

  if (!open || !images.length || !currentImage?.url) {
    return null;
  }

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          style={{
            position: "fixed",
            inset: 0,
            backgroundColor: "rgba(0, 0, 0, 0.9)",
            zIndex: 1300,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
          onClick={handleOverlayClick}
        >
          <Box
            sx={{
              position: "absolute",
              top: 16,
              right: 16,
              display: "flex",
              gap: 1,
              zIndex: 2,
            }}
          >
            <IconButton onClick={zoomOut} disabled={zoom <= MIN_ZOOM} color="inherit">
              <ZoomOutIcon />
            </IconButton>
            <IconButton onClick={resetZoom} disabled={zoom === MIN_ZOOM} color="inherit">
              <CenterFocusStrongIcon />
            </IconButton>
            <IconButton onClick={zoomIn} disabled={zoom >= MAX_ZOOM} color="inherit">
              <ZoomInIcon />
            </IconButton>
            <IconButton onClick={onClose} color="inherit">
              <CloseIcon />
            </IconButton>
          </Box>

          {images.length > 1 && (
            <>
              <IconButton
                onClick={(event) => {
                  event.stopPropagation();
                  goToPrevious();
                }}
                disabled={currentIndex === 0}
                sx={{
                  position: "absolute",
                  left: 24,
                  color: "white",
                  backgroundColor: "rgba(0,0,0,0.4)",
                  "&:hover": { backgroundColor: "rgba(0,0,0,0.6)" },
                }}
              >
                <ArrowBackIosNewIcon />
              </IconButton>
              <IconButton
                onClick={(event) => {
                  event.stopPropagation();
                  goToNext();
                }}
                disabled={currentIndex === images.length - 1}
                sx={{
                  position: "absolute",
                  right: 24,
                  color: "white",
                  backgroundColor: "rgba(0,0,0,0.4)",
                  "&:hover": { backgroundColor: "rgba(0,0,0,0.6)" },
                }}
              >
                <ArrowForwardIosIcon />
              </IconButton>
            </>
          )}

          <Box
            sx={{
              width: MAX_VIEWPORT_WIDTH,
              maxWidth: MAX_VIEWPORT_WIDTH,
              height: MAX_VIEWPORT_HEIGHT,
              maxHeight: MAX_VIEWPORT_HEIGHT,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: zoom > 1 ? (isPanning ? "grabbing" : "grab") : "default",
              overflow: "hidden",
              px: 2,
            }}
            onWheel={handleWheel}
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={stopPanning}
            onMouseLeave={stopPanning}
            onDoubleClick={() => (zoom > 1 ? resetZoom() : zoomIn())}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}
            onTouchCancel={handleTouchEnd}
          >
            {!imageLoaded && (
              <Box
                sx={{
                  position: "absolute",
                  inset: 0,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <CircularProgress color="inherit" />
              </Box>
            )}
            <img
              src={currentImage.url}
              alt={currentImage.title || currentImage.name || "Setup image"}
              onLoad={() => setImageLoaded(true)}
              style={{
                maxWidth: "100%",
                maxHeight: "100%",
                transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoom})`,
                transition: isPanning ? "none" : "transform 0.2s ease",
                display: "block",
                margin: "0 auto",
                pointerEvents: "none",
              }}
            />
          </Box>

          <Box
            sx={{
              position: "absolute",
              bottom: 24,
              textAlign: "center",
              color: "white",
              px: 2,
              py: 1,
              backgroundColor: "rgba(0,0,0,0.4)",
              borderRadius: "8px",
            }}
          >
            <Typography variant="subtitle2">
              {currentImage.title || currentImage.name || `Image ${currentIndex + 1}`}
            </Typography>
            <Typography variant="caption" sx={{ opacity: 0.8 }}>
              {currentIndex + 1} / {images.length}
            </Typography>
          </Box>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default SetupImageLightbox;
